import React, { useCallback, useState } from 'react'
import _ from 'underscore'
import styled from 'styled-components'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import CollapsibleCard from 'components/CollapsibleCard'

import AlertDescriptionModal from 'pages/shared/AlertDescriptionModal'
import MutedNotificationItem from 'pages/shared/NotificationRecipientForm/MutedNotificationsCard/MutedNotificationItem'

import { alertCodeRawValue } from 'helpers/alerts'
import { customerDisplayName } from 'helpers/formatters'
import GA4 from 'helpers/ga4'

import useAlertDescriptions from 'hooks/useAlertDescriptions'
import { useStoreContext } from 'hooks/StoreContext'

import type { MutedCustomerId } from 'types/NotificationRecipient'

type MutedAlertCode = {
  code: string
  description: string
  createdAt: string
  markedForDeletion?: boolean
}

type MutedDeviceIdsForAlertCodes = {
  code: string
  createdAt: string
  customerId: number
  description: string
  deviceId: string
  markedForDeletion?: boolean
}

type MutedNotificationsProps = {
  mutedAlertCodes: Array<MutedAlertCode>
  mutedCustomerIds: Array<MutedCustomerId>
  mutedDeviceIdsForAlertCodes: Array<MutedDeviceIdsForAlertCodes>
  customers: Array<{ id: string; firstName: string; lastName: string; companyName: string }>
  onChange: ({ mutedAlertCodes, mutedCustomerIds, mutedDeviceIdsForAlertCodes }: { mutedAlertCodes: Array<MutedAlertCode>; mutedCustomerIds: Array<MutedCustomerId>; mutedDeviceIdsForAlertCodes: Array<MutedDeviceIdsForAlertCodes> }) => void
  className?: string
}

const MutedNotificationsCard = ({ mutedAlertCodes, mutedCustomerIds, mutedDeviceIdsForAlertCodes, customers, onChange, className }: MutedNotificationsProps) => {
  useAlertDescriptions()

  const { alertDescriptions } = useStoreContext()

  const [mutedAlertCodesMarkedForDeletionState, setMutedAlertCodesMarkedForDeletionState] = useState(mutedAlertCodes)
  const [mutedCustomerIdsMarkedForDeletionState, setMutedCustomerIdsMarkedForDeletionState] = useState(mutedCustomerIds)
  const [mutedDeviceIdsMarkedForDeletionState, setMutedDeviceIdsMarkedForDeletionState] = useState(mutedDeviceIdsForAlertCodes)
  const [selectedAlertCodeForModal, setSelectedAlertCodeForModal] = useState('')

  const count = mutedAlertCodes.length + mutedCustomerIds.length + mutedDeviceIdsForAlertCodes?.length

  const customerName = useCallback((id) => {
    const customer = customers?.find((customer) => customer.id === id)

    return customer && customerDisplayName(customer.firstName, customer.lastName, customer.companyName)
  }, [customers])

  const selectedAlertDescription = _.find(alertDescriptions, (alertDescription) => (
    alertCodeRawValue(alertDescription.code) === alertCodeRawValue(selectedAlertCodeForModal)
  ))

  const onToggleMutedAlertCode = useCallback((code) => {
    GA4.sendEvent({
      category: 'Edit Notification Recipient',
      action: 'Muted Notif. - Toggle Remove Alert Code',
      label: code
    })

    const updatedState = mutedAlertCodesMarkedForDeletionState.map((mutedAlertCode) => {
      if (mutedAlertCode.code === code) {
        mutedAlertCode.markedForDeletion = !mutedAlertCode.markedForDeletion
      }

      return mutedAlertCode
    })

    setMutedAlertCodesMarkedForDeletionState(updatedState)

    onChange({ mutedAlertCodes: updatedState, mutedCustomerIds: mutedCustomerIdsMarkedForDeletionState, mutedDeviceIdsForAlertCodes: mutedDeviceIdsMarkedForDeletionState })
  }, [mutedAlertCodesMarkedForDeletionState, mutedCustomerIdsMarkedForDeletionState, mutedDeviceIdsMarkedForDeletionState, onChange])

  const onToggleMutedDeviceForAlertCode = useCallback((deviceId, code) => {
    const updatedState = mutedDeviceIdsMarkedForDeletionState.map((muteDeviceForAlertCode) => {
      if (muteDeviceForAlertCode.code === code && muteDeviceForAlertCode.deviceId === deviceId) {
        muteDeviceForAlertCode.markedForDeletion = !muteDeviceForAlertCode.markedForDeletion
      }

      return muteDeviceForAlertCode
    })

    setMutedDeviceIdsMarkedForDeletionState(updatedState)

    onChange({ mutedAlertCodes: mutedAlertCodesMarkedForDeletionState, mutedCustomerIds: mutedCustomerIdsMarkedForDeletionState, mutedDeviceIdsForAlertCodes: updatedState })
  }, [mutedAlertCodesMarkedForDeletionState, mutedCustomerIdsMarkedForDeletionState, mutedDeviceIdsMarkedForDeletionState, onChange])

  const onToggleMutedCustomer = useCallback((id) => {
    GA4.sendEvent({
      category: 'Edit Notification Recipient',
      action: 'Muted Notif. - Toggle Remove Customer',
      label: customerName(id)
    })

    const updatedState = mutedCustomerIdsMarkedForDeletionState.map((mutedCustomerId) => {
      if (mutedCustomerId.id === id) {
        mutedCustomerId.markedForDeletion = !mutedCustomerId.markedForDeletion
      }

      return mutedCustomerId
    })

    setMutedCustomerIdsMarkedForDeletionState(updatedState)

    onChange({ mutedAlertCodes: mutedAlertCodesMarkedForDeletionState, mutedCustomerIds: updatedState, mutedDeviceIdsForAlertCodes: mutedDeviceIdsMarkedForDeletionState })
  }, [mutedAlertCodesMarkedForDeletionState, mutedCustomerIdsMarkedForDeletionState, mutedDeviceIdsMarkedForDeletionState, onChange, customerName])

  return (
    <S.CollapsibleCard
      title={`${count} Muted Notification${count === 1 ? '' : 's'}`}
      className={className}
      onClickCaret={() => GA4.sendEvent({ category: 'Edit Notification Recipient', action: 'Muted Notif. - Toggle Panel' })}
      collapsedByDefault
    >
      {count === 0 && <p>To mute an alert, click the Email Subscription Preferences link at the bottom of an alert email.</p>}
      {count > 0 && (
        <>
          {mutedAlertCodes.length > 0 && (
            <S.FirstSection>
              <S.SectionTitle>Alerts Muted for All Customers</S.SectionTitle>
              {mutedAlertCodes.map(({ code, description, createdAt }) => (
                <MutedNotificationItem
                  key={code}
                  name={code}
                  markedForDeletion={!!mutedAlertCodesMarkedForDeletionState.find((mutedAlertCode) => mutedAlertCode.code === code)?.markedForDeletion}
                  createdAt={createdAt}
                  onToggleMuteCallback={() => onToggleMutedAlertCode(code)}
                >
                  <S.Code>{code}</S.Code>
                  <Button decoration="textOnly" text={description} styles={linkButtonStyle} onPress={() => setSelectedAlertCodeForModal(code)} />
                </MutedNotificationItem>
              ))}
            </S.FirstSection>
          )}

          {mutedCustomerIds.length > 0 && (
            <div>
              <S.SectionTitle>Customers with All Alerts Muted</S.SectionTitle>
              {mutedCustomerIds.map(({ id, createdAt }) => (
                <MutedNotificationItem
                  key={id}
                  name={customerName(id)}
                  markedForDeletion={!!mutedCustomerIdsMarkedForDeletionState.find((mutedCustomerId) => mutedCustomerId.id === id)?.markedForDeletion}
                  createdAt={createdAt}
                  onToggleMuteCallback={() => onToggleMutedCustomer(id)}
                >
                  <span>{customerName(id)}</span>
                </MutedNotificationItem>
              ))}
            </div>
          )}
          {mutedDeviceIdsForAlertCodes.length > 0 && (
            <div>
              <S.SectionTitle>Device with Specific Alerts Muted</S.SectionTitle>
              {mutedDeviceIdsForAlertCodes.map(({ customerId, deviceId, createdAt, code, description }) => (
                <MutedNotificationItem
                  key={deviceId + code}
                  name={`${deviceId}-${code}`}
                  markedForDeletion={!!mutedDeviceIdsMarkedForDeletionState.find((mutedDeviceIdsForAlertCodes) => mutedDeviceIdsForAlertCodes.deviceId === deviceId)?.markedForDeletion}
                  createdAt={createdAt}
                  onToggleMuteCallback={() => onToggleMutedDeviceForAlertCode(deviceId, code)}
                >
                  <span>{`${customerName(customerId)} - AUID: ${deviceId} `}</span>
                  <S.Code>{code}</S.Code>
                  <Button decoration="textOnly" text={description} styles={linkButtonStyle} onPress={() => setSelectedAlertCodeForModal(code)} />
                </MutedNotificationItem>
              ))}
            </div>
          )}
        </>
      )}

      <AlertDescriptionModal
        isDisplayed={!!selectedAlertCodeForModal}
        closeCallback={() => { setSelectedAlertCodeForModal('') }}
        alertDescription={selectedAlertDescription}
      />
    </S.CollapsibleCard>
  )
}

export default MutedNotificationsCard

const linkButtonStyle: ButtonStyles = {
  button: {
    height: 'auto',
    marginLeft: 5,
    padding: 0
  }
}

const S = {
  CollapsibleCard: styled(CollapsibleCard)`
    margin-top: 10px;
  `,
  FirstSection: styled.div`
    margin-bottom: 25px;
  `,
  SectionTitle: styled.h3`
    margin: 15px 0;
    text-transform: none;
  `,
  Code: styled.span`
    font-weight: 600;
  `
}
